import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Modal Props
 * @property isOpen - Controls modal visibility
 * @property onClose - Callback when modal should close
 * @property title - Modal header title (optional)
 * @property icon - FontAwesome icon for header (optional)
 * @property iconColor - Tailwind color class for icon (default: "text-blue-600")
 * @property size - Modal width size: "sm" | "md" | "lg" | "xl" | "full" (default: "md")
 * @property children - Modal content (wrap content in a div with padding, e.g., <div className="p-6">)
 * @property footer - Modal footer content (optional)
 * @property closeOnOverlayClick - Allow closing by clicking backdrop (default: true)
 */
interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    icon?: any;
    iconColor?: string;
    size?: "sm" | "md" | "lg" | "xl" | "full";
    children: React.ReactNode;
    footer?: React.ReactNode;
    closeOnOverlayClick?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    icon,
    iconColor = "text-blue-600",
    size = "md",
    children,
    footer,
    closeOnOverlayClick = true,
}) => {
    React.useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: "max-w-md",
        md: "max-w-lg",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
        full: "max-w-9/10",
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && closeOnOverlayClick) {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            {/* Background overlay */}
            <div 
                className="fixed inset-0 transition-opacity duration-300" 
                aria-hidden="true" 
                onClick={handleOverlayClick}
                style={{ backgroundColor: 'rgba(17, 24, 39, 0.75)' }}
            ></div>

            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                {/* Center modal */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                    &#8203;
                </span>

                <div className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle ${sizeClasses[size]} w-full relative z-10`}>
                    {/* Header */}
                    {title && (
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    {icon && <FontAwesomeIcon icon={icon} className={`w-5 h-5 mr-2 ${iconColor}`} />}
                                    <h3 className="text-lg font-semibold text-gray-900" id="modal-title">
                                        {title}
                                    </h3>
                                </div>
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close</span>
                                    <FontAwesomeIcon icon={["fad", "times"]} className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Body */}
                    <div className="bg-white">{children}</div>

                    {/* Footer */}
                    {footer && <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">{footer}</div>}
                </div>
            </div>
        </div>
    );
};

interface ModalBodyProps {
    children: React.ReactNode;
    className?: string;
}

export const ModalBody: React.FC<ModalBodyProps> = ({ children, className = "" }) => {
    return <div className={className}>{children}</div>;
};

interface ModalFooterProps {
    children: React.ReactNode;
    className?: string;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className = "" }) => {
    return <div className={`flex justify-end space-x-3 ${className}`}>{children}</div>;
};

// Button component to replace reactstrap Button
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "primary" | "secondary" | "danger" | "success" | "warning" | "info" | "outline-primary" | "outline-secondary" | "outline-danger" | "outline-success";
    size?: "sm" | "md" | "lg";
    children: React.ReactNode;
    fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({ variant = "primary", size = "md", children, fullWidth = false, className = "", disabled, ...props }) => {
    const baseClasses = "font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center";

    const sizeClasses = {
        sm: "px-3 py-1.5 text-xs",
        md: "px-4 py-2 text-sm",
        lg: "px-6 py-3 text-base",
    };

    const variantClasses = {
        primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
        secondary: "bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500",
        danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
        success: "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500",
        warning: "bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500",
        info: "bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-400",
        "outline-primary": "border border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500",
        "outline-secondary": "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-gray-500",
        "outline-danger": "border border-red-600 text-red-600 hover:bg-red-50 focus:ring-red-500",
        "outline-success": "border border-green-600 text-green-600 hover:bg-green-50 focus:ring-green-500",
    };

    const disabledClasses = disabled ? "opacity-50 cursor-not-allowed" : "";
    const widthClasses = fullWidth ? "w-full" : "";

    return (
        <button className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${disabledClasses} ${widthClasses} ${className}`} disabled={disabled} {...props}>
            {children}
        </button>
    );
};

// Form components
interface FormGroupProps {
    children: React.ReactNode;
    className?: string;
}

export const FormGroup: React.FC<FormGroupProps> = ({ children, className = "" }) => {
    return <div className={`mb-4 ${className}`}>{children}</div>;
};

interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
    children: React.ReactNode;
    required?: boolean;
}

export const Label: React.FC<LabelProps> = ({ children, required, className = "", ...props }) => {
    return (
        <label className={`block text-sm font-medium text-gray-700 mb-1 ${className}`} {...props}>
            {children}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
    );
};

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> {
    type?: "text" | "email" | "password" | "number" | "tel" | "url" | "search" | "date" | "time" | "datetime-local" | "month" | "week" | "textarea" | "select" | "checkbox" | "radio" | "color";
    error?: string;
    rows?: number;
}

export const Input: React.FC<InputProps> = ({ type = "text", error, className = "", rows = 3, children, ...props }) => {
    const baseClasses = "block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm";
    const errorClasses = error ? "border-red-300 focus:border-red-500 focus:ring-red-500" : "";
    const inputClasses = "px-3 py-2"; // Standard padding for inputs
    const textareaClasses = "px-3 py-2"; // Standard padding for textareas

    if (type === "checkbox" || type === "radio") {
        return <input type={type} className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${className}`} {...(props as React.InputHTMLAttributes<HTMLInputElement>)} />;
    }

    if (type === "color") {
        return <input type="color" className={`h-10 cursor-pointer border border-gray-300 rounded ${className}`} {...(props as React.InputHTMLAttributes<HTMLInputElement>)} />;
    }

    if (type === "textarea") {
        return (
            <div>
                <textarea className={`${baseClasses} ${textareaClasses} ${errorClasses} ${className}`} rows={rows} {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)} />
                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
            </div>
        );
    }

    if (type === "select") {
        return (
            <div>
                <select className={`${baseClasses} ${inputClasses} ${errorClasses} ${className}`} {...(props as React.SelectHTMLAttributes<HTMLSelectElement>)}>
                    {children}
                </select>
                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
            </div>
        );
    }

    return (
        <div>
            <input type={type} className={`${baseClasses} ${inputClasses} ${errorClasses} ${className}`} {...(props as React.InputHTMLAttributes<HTMLInputElement>)} />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    );
};

// Table component
interface TableProps {
    children: React.ReactNode;
    className?: string;
}

export const Table: React.FC<TableProps> = ({ children, className = "" }) => {
    return <table className={`min-w-full divide-y divide-gray-200 ${className}`}>{children}</table>;
};
